import { Fragment, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Save,
  Sparkles,
  Mic,
  MicOff,
  ChevronDown,
  Download,
  Loader2,
  FileText,
  Bot,
  RotateCcw,
  Pencil,
  Trash2,
  Plus,
  Info,
  Eye,
  Lightbulb,
  LucideSpeech,
  Zap,
  Crown,
  Check,
  Clock,
  FileSignature,
  Stars,
} from "lucide-react";
import { toast } from "sonner";

import ProgressSteps from "../components/ProgressSteps";
import InvoicePreview from "../components/InvoicePreview";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

import { motorStep, motorCommit } from "../lib/api";
import { listarClientes } from "../lib/clientsApi";
import MotorAnswers from "../components/MotorAnswers";
import EditLineModal from "../components/EditLineModal";
import AddPartidaModal from "../components/AddPartidaModal";
import JobCardDetailModal from "../components/JobCardDetailModal";
import AIVideoPreview from "../components/AIVideoPreview";
import RelatedSuggestionsPanel from "../components/RelatedSuggestionsPanel";
import ProyectoIntegralTipoModal from "../components/ProyectoIntegralTipoModal";
import ProyectoIntegralFormModal from "../components/ProyectoIntegralFormModal";
import ProyectoDetalladoPanel from "../components/ProyectoDetalladoPanel";
import MotorThinkingOverlay from "../components/MotorThinkingOverlay";
import {
  agruparLineasPorCapitulo,
  ordenarLineasConCapitulos,
} from "../lib/capitulos";
import { fmtEUR } from "../lib/storage";
import {
  guardarPresupuesto,
  obtenerPresupuesto,
  nuevoPresupuestoId,
} from "../lib/budgetsApi";
import { exportBudgetPDF } from "../lib/pdf";
import { registerWizard, unregisterWizard } from "../lib/wizardBus";
import {
  publicarSnapshotAnotador,
  limpiarSnapshotAnotador,
} from "../lib/anotadorContext";

const STEPS = ["Cliente", "Servicios", "Confirmar"];

const EMPTY_CLIENT = { id: "", nombre: "", nif: "", direccion: "", telefono: "", email: "" };

const EMPTY_MOTOR = {
  input: "",
  state: {},
  result: null,
  promptEnviado: "",
  loading: false,
  error: null,
};

export default function BudgetWizard() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [step, setStep] = useState(0);
  const [budget, setBudget] = useState(() => ({
    id: id || nuevoPresupuestoId(),
    cliente: { ...EMPTY_CLIENT },
    tipo: "rapido",
    escala: "habituales",
    proyectoIntegral: null,
    estado: "draft",
    fecha: new Date().toISOString(),
    descripcion: "",
    partidas: [],
  }));
  const [cargandoBudget, setCargandoBudget] = useState(!!id);
  const [guardando, setGuardando] = useState(false);

  const [motor, setMotor] = useState(EMPTY_MOTOR);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [proyectoIntegralFase, setProyectoIntegralFase] = useState(null);
  const [proyectoIntegralBorrador, setProyectoIntegralBorrador] = useState(null);

  useEffect(() => {
    if (!id) return;
    let cancelado = false;
    setCargandoBudget(true);
    obtenerPresupuesto(id)
      .then((data) => {
        if (cancelado) return;
        setBudget({
          id: data.id,
          cliente: { ...EMPTY_CLIENT, ...(data.cliente || {}) },
          tipo: data.tipo ?? "rapido",
          escala: data.escala ?? "habituales",
          proyectoIntegral: data.proyectoIntegral ?? null,
          estado: data.estado ?? "draft",
          fecha: data.fecha || new Date().toISOString(),
          descripcion: data.descripcion ?? "",
          partidas: Array.isArray(data.partidas) ? data.partidas : [],
        });
        if ((data.partidas?.length ?? 0) > 0) setStep(2);
      })
      .catch((err) => {
        if (cancelado) return;
        if (err?.response?.status !== 404) {
          console.error("budget_load_error", err);
          toast.error("No se pudo cargar el presupuesto");
        }
      })
      .finally(() => {
        if (!cancelado) setCargandoBudget(false);
      });
    return () => {
      cancelado = true;
    };
  }, [id]);

  const abrirProyectoIntegral = () => {
    setProyectoIntegralBorrador(budget.proyectoIntegral ?? null);
    setProyectoIntegralFase("tipo");
  };

  const cerrarProyectoIntegral = () => {
    setProyectoIntegralFase(null);
    setProyectoIntegralBorrador(null);
  };

  const handlePITipoContinuar = (tipoProyecto) => {
    setProyectoIntegralBorrador((prev) => ({
      ...(prev ?? {}),
      tipoProyecto,
    }));
    setProyectoIntegralFase("form");
  };

  const handlePIFormVolver = () => {
    setProyectoIntegralFase("tipo");
  };

  const handlePIFormSubmit = (datos) => {
    update({ escala: "gran_escala", proyectoIntegral: datos });
    setProyectoIntegralFase(null);
    setProyectoIntegralBorrador(null);
    toast.success("Contexto del proyecto integral guardado");
  };

  const update = (patch) => setBudget((b) => ({ ...b, ...patch }));
  const updateCliente = (patch) =>
    setBudget((b) => ({ ...b, cliente: { ...b.cliente, ...patch } }));

  const handleSave = async (estado = "draft") => {
    const next = { ...budget, estado };
    setGuardando(true);
    try {
      const data = await guardarPresupuesto(next.id, next);
      setBudget((prev) => ({ ...prev, ...data }));
      toast.success(estado === "final" ? "Presupuesto finalizado" : "Borrador guardado");
      return data;
    } catch (err) {
      console.error("budget_save_error", err);
      toast.error("No se pudo guardar el presupuesto");
      throw err;
    } finally {
      setGuardando(false);
    }
  };

  useEffect(() => {
    registerWizard({
      id: budget.id,
      save: async () => {
        const next = { ...budget, estado: "draft" };
        try {
          await guardarPresupuesto(next.id, next);
        } catch (err) {
          console.error("budget_register_save_error", err);
        }
      },
      discard: () => {},
    });
    return () => unregisterWizard();
  }, [budget]);

  useEffect(() => {
    const fase =
      step === 0
        ? "step1_intenciones"
        : step === 1
          ? "step2_preguntas"
          : "step3_presupuesto";
    publicarSnapshotAnotador({
      fase,
      escala: budget.escala ?? null,
      proyectoIntegral: budget.proyectoIntegral ?? null,
      prompt: motor.promptEnviado || budget.descripcion || "",
      intenciones: motor.state?.intenciones ?? [],
      preguntas: motor.result?.data?.questions ?? [],
      partidas: budget.partidas ?? [],
    });
    return () => {
      limpiarSnapshotAnotador();
    };
  }, [
    step,
    budget.escala,
    budget.proyectoIntegral,
    budget.descripcion,
    budget.partidas,
    motor.promptEnviado,
    motor.state,
    motor.result,
  ]);

  const handleFinishAndExport = async () => {
    try {
      await handleSave("final");
      exportBudgetPDF(budget);
      navigate("/");
    } catch {
      // toast ya gestionado en handleSave
    }
  };

  const sincronizarPartidas = (data) => {
    const lineas = ordenarLineasConCapitulos(
      data?.presupuestoIntenciones?.lineas ?? [],
    );
    const partidas = lineas.map((l, idx) => ({
      id: `motor-${idx}-${l.jobcard_id ?? l.area}`,
      area: l.area ?? null,
      jobcard_id: l.jobcard_id ?? null,
      concepto: l.concepto,
      unidad: l.unidad ?? "ud",
      cantidad: Number(l.cantidad) || 0,
      precio_unitario: Number(l.precio_unitario) || 0,
    }));
    update({ partidas });
  };

  const motorEnviarPrompt = async (texto) => {
    if (!texto?.trim()) {
      toast.error("Escribe el prompt antes de enviar al motor");
      return;
    }
    setMotor((m) => ({ ...m, loading: true, error: null, input: texto }));
    update({ descripcion: texto });
    try {
      const initialState = {};
      if (budget.escala === "gran_escala" && budget.proyectoIntegral) {
        initialState.proyectoIntegral = budget.proyectoIntegral;
      }
      console.log("[BudgetWizard.motorEnviarPrompt] state enviado", {
        escala: budget.escala,
        tieneProyectoIntegral: !!budget.proyectoIntegral,
        proyectoIntegral: initialState.proyectoIntegral ?? null,
      });
      const espera = new Promise((resolve) => setTimeout(resolve, 3000));
      const [result] = await Promise.all([
        motorStep({ input: texto, state: initialState }),
        espera,
      ]);
      const nextState = result?.data?.state ?? {};
      setMotor((m) => ({
        ...m,
        loading: false,
        result,
        state: nextState,
        promptEnviado: texto,
      }));
      if (result.type === "complete") {
        sincronizarPartidas(result.data);
        setStep(2);
      }
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.message || "error_motor";
      setMotor((m) => ({ ...m, loading: false, error: msg }));
      toast.error(`Motor: ${msg}`);
    }
  };

  const motorConfirmarRespuestas = async (choices) => {
    setMotor((m) => ({ ...m, loading: true, error: null }));
    try {
      const result = await motorCommit({ state: motor.state, choices });
      const nextState = result?.data?.state ?? motor.state;
      setMotor((m) => ({ ...m, loading: false, result, state: nextState }));
      if (result.type === "complete") {
        sincronizarPartidas(result.data);
        setStep(2);
      }
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.message || "error_motor";
      setMotor((m) => ({ ...m, loading: false, error: msg }));
      toast.error(`Motor: ${msg}`);
    }
  };

  const motorRecommit = async (nuevasIntenciones) => {
    const prevState = motor.state;
    const prevResult = motor.result;
    const intencionesNormalizadas = (nuevasIntenciones ?? [])
      .map((i) => {
        const candidatas = i.candidatas ?? [];
        if (candidatas.length === 0) return null;
        const jobcardValido =
          i.jobcard_id != null && candidatas.some((c) => c.id === i.jobcard_id)
            ? i.jobcard_id
            : candidatas[0].id;
        const cantidadNum = Number(i.cantidad);
        const cantidadValida =
          Number.isFinite(cantidadNum) && cantidadNum > 0
            ? cantidadNum
            : Number(i.cantidad_sugerida) > 0
              ? Number(i.cantidad_sugerida)
              : 1;
        return { ...i, jobcard_id: jobcardValido, cantidad: cantidadValida };
      })
      .filter(Boolean);
    const nextState = { ...motor.state, intenciones: intencionesNormalizadas };
    setMotor((m) => ({ ...m, loading: true, error: null, state: nextState }));
    try {
      const result = await motorCommit({ state: nextState, choices: [] });
      if (result.type === "complete") {
        const finalState = result?.data?.state ?? nextState;
        setMotor((m) => ({ ...m, loading: false, result, state: finalState }));
        sincronizarPartidas(result.data);
      } else {
        console.warn("motor_recommit_questions", {
          enviadas: intencionesNormalizadas,
          respuesta: result,
        });
        setMotor((m) => ({ ...m, loading: false, state: prevState, result: prevResult }));
        toast.error("No se pudo aplicar el cambio: hay opciones sin resolver");
      }
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.message || "error_motor";
      setMotor((m) => ({ ...m, loading: false, state: prevState, result: prevResult, error: msg }));
      toast.error(`Motor: ${msg}`);
    }
  };

  const motorEditLine = async ({ area, jobcard_id, cantidad }) => {
    const intenciones = (motor.state.intenciones ?? []).map((i) =>
      i.area === area ? { ...i, jobcard_id, cantidad } : i,
    );
    await motorRecommit(intenciones);
  };

  const motorDeleteLine = async (area) => {
    const intenciones = (motor.state.intenciones ?? []).filter((i) => i.area !== area);
    if (intenciones.length === 0) {
      toast("Has eliminado todas las partidas");
    }
    await motorRecommit(intenciones);
  };

  const construirIntencionDesdeJobcard = (jobcard, cantidad) => {
    const baseArea = (jobcard.familia ?? "extra").toLowerCase();
    const existentes = new Set((motor.state.intenciones ?? []).map((i) => i.area));
    let area = `extra_${baseArea}_${jobcard.id.toLowerCase()}`;
    let suffix = 1;
    while (existentes.has(area)) {
      area = `extra_${baseArea}_${jobcard.id.toLowerCase()}_${suffix++}`;
    }
    return {
      area,
      query: jobcard.texto?.split(".")[0]?.slice(0, 80) ?? jobcard.id,
      cantidad: Number(cantidad) || 1,
      unidad: jobcard.unidad ?? "ud",
      candidatas: [jobcard],
      jobcard_id: jobcard.id,
    };
  };

  const motorAddPartida = async ({ jobcard, cantidad }) => {
    const nuevaIntencion = construirIntencionDesdeJobcard(jobcard, cantidad);
    const intenciones = [...(motor.state.intenciones ?? []), nuevaIntencion];
    await motorRecommit(intenciones);
  };

  const motorAddPartidaInline = ({ jobcard, cantidad }) => {
    const nuevaIntencion = construirIntencionDesdeJobcard(jobcard, cantidad);
    setMotor((m) => ({
      ...m,
      state: {
        ...m.state,
        intenciones: [...(m.state.intenciones ?? []), nuevaIntencion],
      },
    }));
    toast.success("Partida añadida. Se incluirá al confirmar las respuestas.");
  };

  const motorModifyWithAI = async (prompt) => {
    if (!prompt?.trim()) return;
    setMotor((m) => ({ ...m, loading: true, error: null }));
    try {
      const res = await motorStep({ input: prompt, state: {} });
      const nuevasIntenciones =
        res?.data?.intenciones ?? res?.data?.state?.intenciones ?? [];
      const existentes = new Set(
        (motor.state.intenciones ?? []).map((i) => i.area),
      );
      const extras = [];
      for (const i of nuevasIntenciones) {
        const candidatas = i.candidatas ?? [];
        const candidata =
          candidatas.find((c) => c.id === i.jobcard_id) ?? candidatas[0];
        if (!candidata) continue;
        const baseArea = (candidata.familia ?? "extra").toLowerCase();
        let area = `extra_${baseArea}_${candidata.id.toLowerCase()}`;
        let suffix = 1;
        while (existentes.has(area)) {
          area = `extra_${baseArea}_${candidata.id.toLowerCase()}_${suffix++}`;
        }
        existentes.add(area);
        const cantidadNum = Number(i.cantidad);
        const cantidad =
          Number.isFinite(cantidadNum) && cantidadNum > 0 ? cantidadNum : 1;
        extras.push({
          area,
          query:
            candidata.texto?.split(".")[0]?.slice(0, 80) ??
            i.query ??
            candidata.id,
          cantidad,
          unidad: i.unidad ?? candidata.unidad ?? "ud",
          candidatas: [candidata],
          jobcard_id: candidata.id,
        });
      }

      if (extras.length === 0) {
        setMotor((m) => ({ ...m, loading: false }));
        toast.error("La IA no encontró partidas para añadir");
        return false;
      }

      setMotor((m) => ({
        ...m,
        loading: false,
        state: {
          ...m.state,
          intenciones: [...(m.state.intenciones ?? []), ...extras],
        },
      }));
      toast.success(
        extras.length === 1
          ? "1 partida añadida"
          : `${extras.length} partidas añadidas`,
      );
      return true;
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.message || "error_motor";
      setMotor((m) => ({ ...m, loading: false, error: msg }));
      toast.error(`Modificar con IA: ${msg}`);
      return false;
    }
  };

  const motorRemovePartidaInline = (area) => {
    setMotor((m) => ({
      ...m,
      state: {
        ...m.state,
        intenciones: (m.state.intenciones ?? []).filter((i) => i.area !== area),
      },
    }));
    toast("Partida descartada");
  };

  const motorReset = () => {
    setMotor(EMPTY_MOTOR);
    update({ partidas: [] });
  };

  if (cargandoBudget) {
    return (
      <div className="px-10 py-20 max-w-5xl mx-auto text-center" data-testid="budget-wizard-loading">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-3" />
        <p className="text-sm text-slate-500">Cargando presupuesto…</p>
      </div>
    );
  }

  const wizardMaxWidth = step === 1 ? "max-w-7xl" : "max-w-5xl";

  return (
    <div
      className={`px-10 py-10 ${wizardMaxWidth} mx-auto`}
      data-testid="budget-wizard"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
          {id ? "Editar presupuesto" : "Nuevo presupuesto"} · {budget.id}
        </div>
        <div className="flex items-center gap-1">
          {step === 2 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPreviewOpen(true)}
              className="gap-2 text-slate-500"
              data-testid="open-preview-btn"
            >
              <Eye className="w-4 h-4" /> Vista previa
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleSave("draft")}
            disabled={guardando || cargandoBudget}
            className="gap-2 text-slate-500"
            data-testid="save-draft-btn"
          >
            {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {guardando ? "Guardando…" : "Guardar borrador"}
          </Button>
        </div>
      </div>

      <ProgressSteps steps={STEPS} current={step} />

      {step === 0 && (
        <Step1Cliente
          cliente={budget.cliente}
          tipo={budget.tipo}
          updateCliente={updateCliente}
          updateTipo={(tipo) => update({ tipo })}
        />
      )}
      {step === 1 && (
        <Step2Servicios
          motor={motor}
          budget={budget}
          escala={budget.escala ?? "habituales"}
          updateEscala={(escala) => update({ escala })}
          proyectoIntegral={budget.proyectoIntegral ?? null}
          onAbrirProyectoIntegral={abrirProyectoIntegral}
          onEnviarPrompt={motorEnviarPrompt}
          onConfirmarRespuestas={motorConfirmarRespuestas}
          onReset={motorReset}
          onAddPartida={motorAddPartidaInline}
          onRemovePartida={motorRemovePartidaInline}
          onModifyWithAI={motorModifyWithAI}
        />
      )}
      {step === 2 && (
        <Step3Confirmar
          budget={budget}
          motor={motor}
          update={update}
          onSave={() => handleSave("draft")}
          onExport={handleFinishAndExport}
          onEditLine={motorEditLine}
          onDeleteLine={motorDeleteLine}
          onAddPartida={motorAddPartida}
        />
      )}

      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent
          side="right"
          className="sm:max-w-[920px] w-full overflow-y-auto"
          data-testid="preview-sheet"
        >
          <SheetHeader>
            <SheetTitle className="font-display font-bold text-xl">
              Vista previa del presupuesto
            </SheetTitle>
          </SheetHeader>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
            <div>
              <h3 className="font-display font-bold text-lg mb-3">Vista previa</h3>
              <InvoicePreview budget={budget} />
            </div>
            <div className="flex flex-col gap-3">
              <AIVideoPreview trigger={(budget.partidas ?? []).length} />
              <div className="flex flex-col gap-2">
                <Button
                  onClick={handleFinishAndExport}
                  className="bg-blue-600 hover:bg-blue-700 gap-2 w-full"
                  data-testid="export-pdf-btn"
                >
                  <Download className="w-4 h-4" /> Descargar PDF
                </Button>
                <Button
                  onClick={() => handleSave("draft")}
                  variant="outline"
                  className="gap-2 w-full border-slate-300"
                  data-testid="save-only-btn"
                >
                  <FileText className="w-4 h-4" /> Guardar borrador
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <ProyectoIntegralTipoModal
        open={proyectoIntegralFase === "tipo"}
        onClose={cerrarProyectoIntegral}
        onContinuar={handlePITipoContinuar}
        tipoInicial={
          proyectoIntegralBorrador?.tipoProyecto ??
          budget.proyectoIntegral?.tipoProyecto ??
          null
        }
      />

      <ProyectoIntegralFormModal
        open={proyectoIntegralFase === "form"}
        onClose={cerrarProyectoIntegral}
        onVolver={handlePIFormVolver}
        onSubmit={handlePIFormSubmit}
        datosIniciales={proyectoIntegralBorrador ?? budget.proyectoIntegral}
      />

      <div className="flex justify-between mt-8">
        <Button
          variant="outline"
          onClick={() => (step === 0 ? navigate("/") : setStep(step - 1))}
          className="gap-2 border-slate-300"
          data-testid="wizard-prev-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {step === 0 ? "Volver al inicio" : "Atrás"}
        </Button>

        {step === 0 && (
          <Button
            onClick={() => {
              if (!budget.cliente?.nombre) {
                toast.error("Indica al menos el nombre del cliente");
                return;
              }
              setStep(1);
            }}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="wizard-next-btn"
          >
            Siguiente <ArrowRight className="w-4 h-4" />
          </Button>
        )}

        {step === 2 && (
          <Button
            onClick={handleFinishAndExport}
            disabled={!budget.partidas?.length}
            className="bg-blue-600 hover:bg-blue-700 gap-2"
            data-testid="wizard-finish-btn"
          >
            Finalizar y descargar PDF <Download className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* -------------------- Step 1 · Cliente -------------------- */
function Step1Cliente({ cliente, tipo, updateCliente, updateTipo }) {
  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [cargandoClientes, setCargandoClientes] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCargandoClientes(true);
    listarClientes()
      .then((data) => {
        if (!cancelado) setClientesDisponibles(data);
      })
      .catch((err) => {
        console.error("step1_clients_load_error", err);
        if (!cancelado) toast.error("No se pudieron cargar los clientes");
      })
      .finally(() => {
        if (!cancelado) setCargandoClientes(false);
      });
    return () => {
      cancelado = true;
    };
  }, []);

  return (
    <div className="rx-card p-8" data-testid="step-1">
      <h2 className="font-display font-bold text-2xl mb-1">Datos del cliente</h2>
      <p className="text-slate-500 text-sm mb-6">
        Selecciona uno existente o introduce uno nuevo. Puedes gestionar tu cartera completa en la sección Clientes.
      </p>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 block">
            Cliente existente
          </Label>
          <Link
            to="/clientes"
            className="text-xs text-blue-600 hover:text-blue-700 font-semibold"
          >
            Gestionar clientes →
          </Link>
        </div>
        <Select
          value={cliente.id || ""}
          onValueChange={(v) => {
            const c = clientesDisponibles.find((x) => x.id === v);
            if (c) {
              updateCliente({
                id: c.id,
                nombre: c.nombre || "",
                nif: c.nif || "",
                direccion: c.direccion || "",
                telefono: c.telefono || "",
                email: c.email || "",
              });
            }
          }}
          disabled={cargandoClientes}
        >
          <SelectTrigger className="w-full" data-testid="client-select">
            <SelectValue
              placeholder={
                cargandoClientes
                  ? "Cargando clientes…"
                  : clientesDisponibles.length === 0
                    ? "Aún no tienes clientes — créalos en la sección Clientes"
                    : "Elige un cliente o rellena abajo"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {clientesDisponibles.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`client-option-${c.id}`}>
                {c.nombre}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Field label="Nombre / Razón social" testid="client-name">
          <Input
            value={cliente.nombre || ""}
            readOnly
            disabled
            placeholder="—"
            data-testid="client-name-input"
            className="bg-slate-50 text-slate-700 cursor-not-allowed"
          />
        </Field>
        <Field label="NIF / CIF" testid="client-nif">
          <Input
            value={cliente.nif || ""}
            readOnly
            disabled
            placeholder="—"
            data-testid="client-nif-input"
            className="bg-slate-50 text-slate-700 cursor-not-allowed"
          />
        </Field>
        <Field label="Teléfono" testid="client-phone">
          <Input
            value={cliente.telefono || ""}
            readOnly
            disabled
            placeholder="—"
            data-testid="client-phone-input"
            className="bg-slate-50 text-slate-700 cursor-not-allowed"
          />
        </Field>
        <Field label="Email" testid="client-email">
          <Input
            type="email"
            value={cliente.email || ""}
            readOnly
            disabled
            placeholder="—"
            data-testid="client-email-input"
            className="bg-slate-50 text-slate-700 cursor-not-allowed"
          />
        </Field>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        Los datos del cliente se gestionan en la sección Clientes. Para modificarlos, edítalos allí y vuelve.
      </p>

      <div className="mt-6 pt-5 border-t border-slate-200">
        <div className="flex items-baseline justify-between mb-2">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block">
            Tipo de presupuesto
          </Label>
          <span className="text-[11px] text-slate-400">
            Elige cómo presentarlo al cliente
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 rx-stagger">
          <BudgetTipoCard
            active={tipo === "rapido"}
            onClick={() => updateTipo("rapido")}
            icon={Zap}
            iconBg="from-blue-500 to-cyan-500"
            title="Rápido"
            tagline="Crea y envía en menos de 1 minuto"
            badge={{ label: "Recomendado", icon: Stars, tone: "blue" }}
            estimatedTime="~ 1 min"
            features={[
              { icon: Check, text: "Datos del cliente" },
              { icon: Check, text: "Partidas y precios con IA" },
              { icon: Check, text: "PDF con portada profesional" },
            ]}
            testid="tipo-rapido"
          />
          <BudgetTipoCard
            active={tipo === "completo"}
            onClick={() => updateTipo("completo")}
            icon={Crown}
            iconBg="from-purple-600 via-fuchsia-500 to-pink-500"
            title="Completo"
            tagline="Presupuesto premium con todo el detalle"
            badge={{ label: "Pro", icon: Crown, tone: "purple" }}
            estimatedTime="~ 3 min"
            features={[
              { icon: Check, text: "Todo lo del modo rápido" },
              { icon: FileSignature, text: "Descripción del trabajo" },
              { icon: FileSignature, text: "Condiciones específicas" },
              { icon: FileText, text: "Fichas técnicas adjuntas" },
              { icon: Lightbulb, text: "Notas internas" },
            ]}
            testid="tipo-completo"
          />
        </div>
      </div>
    </div>
  );
}

function BudgetTipoCard({
  active,
  onClick,
  icon: Icono,
  iconBg,
  title,
  tagline,
  badge,
  estimatedTime,
  features,
  testid,
}) {
  const BadgeIcon = badge?.icon;
  const badgeTone =
    badge?.tone === "purple"
      ? "bg-purple-100 text-purple-700 border-purple-200"
      : "bg-blue-100 text-blue-700 border-blue-200";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`group relative overflow-hidden text-left rounded-xl border bg-white transition-all duration-200
        ${
          active
            ? "border-blue-600 ring-2 ring-blue-500/30 shadow-md"
            : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
        }`}
    >
      <div
        className={`pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full blur-2xl opacity-0 transition-opacity duration-300 bg-gradient-to-br ${iconBg} ${
          active ? "opacity-25" : "group-hover:opacity-15"
        }`}
        aria-hidden="true"
      />

      <div className="relative px-3.5 py-3 flex items-center gap-3">
        <div
          className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-white shadow bg-gradient-to-br ${iconBg}`}
        >
          <Icono className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-bold text-sm tracking-tight text-slate-900 truncate">
              {title}
            </h3>
            {badge ? (
              <span
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border text-[9px] uppercase tracking-wider font-bold ${badgeTone}`}
              >
                {BadgeIcon ? <BadgeIcon className="w-2.5 h-2.5" /> : null}
                {badge.label}
              </span>
            ) : null}
          </div>
          {tagline ? (
            <p className="text-[11px] text-slate-500 leading-tight truncate">
              {tagline}
            </p>
          ) : null}
        </div>
        {estimatedTime ? (
          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-slate-500 font-medium">
            <Clock className="w-2.5 h-2.5" /> {estimatedTime}
          </span>
        ) : null}
      </div>

      {features?.length ? (
        <div className="relative px-3.5 pb-3 -mt-0.5 flex flex-wrap gap-1">
          {features.map((f, i) => (
            <span
              key={i}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] leading-none transition-colors ${
                active
                  ? "bg-blue-50 text-blue-700 border border-blue-100"
                  : "bg-slate-50 text-slate-600 border border-slate-100 group-hover:bg-slate-100"
              }`}
            >
              <Check className="w-2.5 h-2.5" />
              {f.text}
            </span>
          ))}
        </div>
      ) : null}

      {active ? (
        <span
          className="absolute top-1.5 right-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white shadow-sm"
          aria-hidden="true"
        >
          <Check className="w-2.5 h-2.5" />
        </span>
      ) : null}
    </button>
  );
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? "col-span-2" : ""} data-testid={`field-${testid}`}>
      <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

const PI_TIPO_LABEL = {
  vivienda: "Vivienda",
  local_comercial: "Local comercial",
  rehabilitacion_parcial: "Rehabilitación parcial",
};

const PI_OBRA_LABEL = {
  reforma_integral: "Reforma integral",
  obra_nueva: "Obra nueva",
};

const PI_ACABADOS_LABEL = {
  basica: "Acabados básicos",
  media: "Acabados medios",
  alta: "Acabados altos",
};

function ProyectoIntegralResumen({ proyectoIntegral, onEditar }) {
  if (!proyectoIntegral) return null;
  const chips = [];
  if (proyectoIntegral.tipoProyecto) {
    chips.push(PI_TIPO_LABEL[proyectoIntegral.tipoProyecto] ?? proyectoIntegral.tipoProyecto);
  }
  if (proyectoIntegral.obra) {
    chips.push(PI_OBRA_LABEL[proyectoIntegral.obra] ?? proyectoIntegral.obra);
  }
  if (proyectoIntegral.superficie != null) {
    chips.push(`${proyectoIntegral.superficie} m²`);
  }
  if (proyectoIntegral.numPlantas) {
    const v = String(proyectoIntegral.numPlantas);
    chips.push(v.endsWith("+") ? `${v} plantas` : `${v} planta${v === "1" ? "" : "s"}`);
  }
  if (proyectoIntegral.nivelAcabados) {
    chips.push(PI_ACABADOS_LABEL[proyectoIntegral.nivelAcabados] ?? proyectoIntegral.nivelAcabados);
  }
  const detalles = proyectoIntegral.detalles ?? {};
  if (detalles.subtipoVivienda) chips.push(detalles.subtipoVivienda);
  if (detalles.dormitorios != null) chips.push(`${detalles.dormitorios} dorm.`);
  if (detalles.banos != null) chips.push(`${detalles.banos} baños`);
  if (detalles.subtipoLocal) chips.push(detalles.subtipoLocal);
  if (detalles.aseos != null && detalles.aseos > 0) chips.push(`${detalles.aseos} aseos`);
  if (detalles.cocinaIndustrial) chips.push("Cocina industrial");
  for (const inst of detalles.instalacionesEspeciales ?? []) chips.push(inst);
  if (detalles.zonaRehabilitacion) chips.push(detalles.zonaRehabilitacion);
  if (detalles.superficieAfectada != null)
    chips.push(`${detalles.superficieAfectada} m² afectados`);

  return (
    <div
      className="mt-3 rounded-md border border-purple-200 bg-purple-50/40 p-3"
      data-testid="proyecto-integral-resumen"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[11px] uppercase tracking-widest font-bold text-purple-700">
          Contexto del proyecto integral
        </div>
        <button
          type="button"
          onClick={onEditar}
          className="text-[11px] uppercase tracking-widest font-bold text-purple-700 hover:text-purple-900"
          data-testid="proyecto-integral-editar"
        >
          Editar
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c, i) => (
          <span
            key={`${c}-${i}`}
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-white border border-purple-200 text-[11px] text-slate-700"
          >
            {c}
          </span>
        ))}
      </div>
      {proyectoIntegral.notasAdicionales && (
        <div className="mt-2 text-xs text-slate-600 leading-snug whitespace-pre-wrap">
          {proyectoIntegral.notasAdicionales}
        </div>
      )}
    </div>
  );
}

function TipoOption({ active, onClick, title, desc, bullets, badge, badgeTone = "blue", testid }) {
  const badgeClasses = {
    blue: "bg-blue-100 text-blue-700 border-blue-200",
    purple: "bg-purple-100 text-purple-700 border-purple-200",
    amber: "bg-amber-100 text-amber-700 border-amber-200",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  }[badgeTone] ?? "bg-slate-100 text-slate-600 border-slate-200";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 rounded-md border transition-all h-full flex flex-col justify-start ${
        active
          ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-600"
          : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="font-display font-bold text-base">{title}</span>
        {badge && (
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-widest font-bold ${badgeClasses}`}
          >
            {badge}
          </span>
        )}
      </div>
      {desc && (
        <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
      )}
      {bullets && bullets.length > 0 && (
        <ul className="text-xs text-slate-600 leading-relaxed mt-1.5 space-y-0.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="text-slate-400 mt-[1px]">·</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
    </button>
  );
}

/* -------------------- Step 2 · Servicios (motor) -------------------- */
function Step2Servicios({ motor, budget, escala, updateEscala, proyectoIntegral, onAbrirProyectoIntegral, onEnviarPrompt, onConfirmarRespuestas, onReset, onAddPartida, onRemovePartida, onModifyWithAI }) {
  const [input, setInput] = useState(motor.input || motor.promptEnviado || "");
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);

  const phase = motor.result?.type === "questions" ? "answers" : "prompt";

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Tu navegador no soporta dictado por voz. Usa Chrome.");
      return;
    }
    const r = new SR();
    r.lang = "es-ES";
    r.continuous = true;
    r.interimResults = true;
    let finalText = input;
    r.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalText += " " + event.results[i][0].transcript;
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setInput((finalText + " " + interim).trim());
    };
    r.onerror = (e) => {
      console.error(e);
      toast.error("Error de reconocimiento de voz");
      setRecording(false);
    };
    r.onend = () => setRecording(false);
    r.start();
    recognitionRef.current = r;
    setRecording(true);
  };
  const stopVoice = () => {
    recognitionRef.current?.stop();
    setRecording(false);
  };

  const preguntas = motor.result?.data?.structuredIntencionQuestions ?? [];
  const intencionesActuales = (motor.state?.intenciones ?? []).map((i) => {
    const candidatas = i.candidatas ?? [];
    const elegida =
      candidatas.find((c) => c.id === i.jobcard_id) ?? candidatas[0] ?? null;
    return {
      area: i.area,
      jobcard_id: i.jobcard_id ?? elegida?.id ?? null,
      concepto: elegida?.texto?.split(".")[0]?.slice(0, 120) ?? null,
      familia: elegida?.familia ?? null,
      unidad: i.unidad ?? elegida?.unidad ?? null,
      cantidad: i.cantidad ?? null,
    };
  });
  const excludeJobcardIds = Array.from(
    new Set(
      (motor.state?.intenciones ?? []).flatMap((i) =>
        (i.candidatas ?? []).map((c) => c.id),
      ),
    ),
  );
  const intencionesExtra = (motor.state?.intenciones ?? []).filter(
    (i) => typeof i.area === "string" && i.area.startsWith("extra_"),
  );

  const mostrarPanelSugerencias = phase === "answers" && intencionesActuales.length > 0;

  return (
    <div
      className={`grid gap-4 ${
        mostrarPanelSugerencias ? "lg:grid-cols-3" : "grid-cols-1"
      }`}
      data-testid="step-2-wrapper"
    >
    <div
      className={`rx-card p-5 ${mostrarPanelSugerencias ? "lg:col-span-2" : ""}`}
      data-testid="step-2"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-display font-bold text-lg mb-0.5 flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" /> Describe el trabajo
          </h2>
          <p className="text-slate-500 text-xs">
            {phase === "prompt"
              ? "Cuanto más detalle aportes, mejor te entenderá el motor."
              : "Elige una opción y ajusta cantidades. El total se calcula en vivo."}
          </p>
        </div>
        {phase === "answers" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="gap-1.5 text-slate-500 hover:text-slate-800"
            data-testid="motor-reset-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Empezar de nuevo
          </Button>
        )}
      </div>

      {phase === "prompt" && (
        <>
          <div className="mb-3">
            <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2 block">
              Tipo de proyecto
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <TipoOption
                active={escala === "habituales"}
                onClick={() => updateEscala?.("habituales")}
                title="Proyectos habituales"
                badge="Recomendado"
                badgeTone="blue"
                desc="Para el 90% de los trabajos: averías, instalaciones, reformas parciales."
                testid="escala-habituales"
              />
              <TipoOption
                active={escala === "gran_escala"}
                onClick={() => onAbrirProyectoIntegral?.()}
                title="Obras de gran escala"
                badge="Pro"
                badgeTone="purple"
                desc="Reformas integrales completas, rehabilitaciones o locales comerciales."
                testid="escala-gran-escala"
              />
            </div>
            {escala === "gran_escala" && proyectoIntegral && (
              <ProyectoIntegralResumen
                proyectoIntegral={proyectoIntegral}
                onEditar={onAbrirProyectoIntegral}
              />
            )}
          </div>
          <div className="relative">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ej: Reforma integral de baño de 6 m². Cambiar bañera por plato de ducha, alicatar con porcelánico 60x60, mueble nuevo con lavabo, mampara cristal."
              rows={4}
              className="resize-none text-sm pr-14"
              data-testid="motor-input"
              disabled={motor.loading}
            />
            <button
              type="button"
              onClick={recording ? stopVoice : startVoice}
              data-testid="voice-btn"
              className={`absolute bottom-3 right-3 p-2.5 rounded-full transition-all ${
                recording
                  ? "bg-red-500 text-white rx-mic-pulse"
                  : "bg-white border border-slate-300 text-slate-600 hover:border-blue-600 hover:text-blue-600"
              }`}
              aria-label={recording ? "Detener dictado" : "Dictar"}
            >
              {recording ? <MicOff className="w-4 h-4" /> : <LucideSpeech className="w-4 h-4" />}
            </button>
            <MotorThinkingOverlay visible={motor.loading} />
          </div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            {recording
              ? "Grabando… pulsa para parar."
              : "Pulsa el micrófono para dictar (Chrome)."}
          </div>
          <Button
            onClick={() => onEnviarPrompt(input)}
            className="rx-spark-btn mt-3"
            disabled={motor.loading}
            data-testid="motor-send-btn"
          >
            {motor.loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" /> Procesando…
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" /> Generar partidas con IA
              </>
            )}
          </Button>
        </>
      )}

      {phase === "answers" && (
        <>
          {motor.promptEnviado && (
            <details className="mb-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 group">
              <summary className="cursor-pointer text-[11px] uppercase tracking-widest font-bold text-slate-500 list-none flex items-center justify-between">
                <span>Tu descripción</span>
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-open:rotate-180" />
              </summary>
              <div className="text-xs text-slate-700 whitespace-pre-wrap pt-2 border-t border-slate-200/70 mt-2">{motor.promptEnviado}</div>
            </details>
          )}
          {intencionesExtra.length > 0 && (
            <div className="mb-3 rounded-md border border-purple-200 bg-purple-50/40 px-4 py-3" data-testid="step2-extras">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-3.5 h-3.5 text-purple-600" />
                <span className="text-[10px] uppercase tracking-widest font-bold text-purple-700">
                  Partidas añadidas IA · {intencionesExtra.length}
                </span>
              </div>
              <ul className="flex flex-col gap-2">
                {intencionesExtra.map((i) => {
                  const candidata = (i.candidatas ?? []).find((c) => c.id === i.jobcard_id) ?? i.candidatas?.[0];
                  const titulo = candidata?.texto?.split(".")[0] || i.jobcard_id || i.area;
                  const precio = candidata?.coste_total;
                  const unidad = i.unidad ?? candidata?.unidad ?? "ud";
                  const importe = (Number(i.cantidad) || 0) * Number(precio ?? 0);
                  return (
                    <li
                      key={i.area}
                      className="flex items-start gap-3 bg-white border border-purple-200 rounded-md px-3 py-2.5"
                      data-testid={`step2-extra-${i.area}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800 leading-snug">{titulo}</div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500 tabular-nums">
                          <span>
                            {i.cantidad ?? 1} {unidad}
                          </span>
                          {precio != null ? (
                            <span>
                              · {fmtEUR(Number(precio))} /{unidad}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {precio != null ? (
                        <div className="text-right shrink-0 min-w-[90px]">
                          <div className="text-[9px] uppercase tracking-widest text-slate-400 font-bold leading-none">
                            Importe
                          </div>
                          <div className="font-display font-bold text-sm tabular-nums leading-tight text-blue-700">
                            {fmtEUR(importe)}
                          </div>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => onRemovePartida?.(i.area)}
                        className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50"
                        title="Descartar partida"
                        data-testid={`step2-extra-remove-${i.area}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {preguntas.length > 0 ? (
            <MotorAnswers
              preguntas={preguntas}
              intenciones={motor.state?.intenciones ?? []}
              onSubmit={onConfirmarRespuestas}
              loading={motor.loading}
              onAddPartida={onAddPartida}
              onModifyWithAI={onModifyWithAI}
            />
          ) : (
            <div className="text-sm text-slate-500">No hay preguntas pendientes.</div>
          )}
        </>
      )}

      {motor.error && (
        <div
          className="mt-3 text-sm bg-red-50 border border-red-200 rounded-md p-3 text-red-700"
          data-testid="motor-error"
        >
          {motor.error}
        </div>
      )}
    </div>

    {mostrarPanelSugerencias && (
      <aside className="lg:col-span-1">
        <div className="lg:sticky lg:top-4">
          <RelatedSuggestionsPanel
            contextoPrompt={motor.promptEnviado || budget?.descripcion || ""}
            intencionesActuales={intencionesActuales}
            excludeJobcardIds={excludeJobcardIds}
            proyectoIntegral={proyectoIntegral ?? null}
            onAdd={onAddPartida}
            loading={motor.loading}
            disabled={false}
          />
        </div>
      </aside>
    )}
    </div>
  );
}

/* -------------------- Step 3 · Confirmar -------------------- */
function Step3Confirmar({
  budget,
  motor,
  update,
  onSave,
  onExport,
  onEditLine,
  onDeleteLine,
  onAddPartida,
}) {
  const [openDesc, setOpenDesc] = useState(false);

  return (
    <div className="space-y-6" data-testid="step-3">
      <div className="rx-card p-8">
        <MotorBudgetView
          motor={motor}
          budget={budget}
          update={update}
          loading={motor.loading}
          onEditLine={onEditLine}
          onDeleteLine={onDeleteLine}
          onAddPartida={onAddPartida}
        />
      </div>

      <ProyectoDetalladoPanel
        visible={budget.tipo === "completo"}
        partidas={budget.partidas ?? []}
        proyectoIntegral={budget.proyectoIntegral ?? null}
        contextoPrompt={motor.promptEnviado || budget.descripcion || ""}
      />

      <RelatedSuggestionsPanel
        contextoPrompt={motor.promptEnviado || budget.descripcion || ""}
        proyectoIntegral={budget.proyectoIntegral ?? null}
        intencionesActuales={(motor.state?.intenciones ?? []).map((i) => {
          const candidatas = i.candidatas ?? [];
          const elegida =
            candidatas.find((c) => c.id === i.jobcard_id) ?? candidatas[0] ?? null;
          return {
            area: i.area,
            jobcard_id: i.jobcard_id ?? elegida?.id ?? null,
            concepto: elegida?.texto?.split(".")[0]?.slice(0, 120) ?? null,
            familia: elegida?.familia ?? null,
            unidad: i.unidad ?? elegida?.unidad ?? null,
            cantidad: i.cantidad ?? null,
          };
        })}
        excludeJobcardIds={Array.from(
          new Set(
            (motor.state?.intenciones ?? []).flatMap((i) =>
              (i.candidatas ?? []).map((c) => c.id),
            ),
          ),
        )}
        onAdd={onAddPartida}
        loading={motor.loading}
        disabled={(motor.state?.intenciones ?? []).length === 0}
      />

      <Collapsible open={openDesc} onOpenChange={setOpenDesc}>
        <CollapsibleTrigger asChild>
          <button className="rx-card w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-testid="desc-toggle">
            <span className="font-medium text-slate-700">Notas adicionales (opcional)</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${openDesc ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="rx-card mt-2 p-5">
            <Textarea
              value={budget.descripcion || ""}
              onChange={(e) => update({ descripcion: e.target.value })}
              rows={3}
              placeholder="Notas, observaciones o condiciones particulares…"
              data-testid="step3-desc"
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

    </div>
  );
}

/* -------------------- Vista del presupuesto (motor) -------------------- */
function MotorBudgetView({ motor, budget, update, loading, onEditLine, onDeleteLine, onAddPartida }) {
  const data = motor.result?.data;
  const motorLineas = data?.presupuestoIntenciones?.lineas ?? [];
  const motorTotal = data?.presupuestoIntenciones?.total ?? data?.presupuesto?.total ?? 0;
  const intenciones = motor.state?.intenciones ?? [];
  const promptEnviado = motor.promptEnviado;
  const motorActivo = motorLineas.length > 0 || intenciones.length > 0;

  const partidasGuardadas = budget?.partidas ?? [];
  const lineasOriginales = motorActivo
    ? motorLineas
    : partidasGuardadas.map((p) => ({
        area: p.area ?? p.id,
        jobcard_id: p.jobcard_id ?? null,
        concepto: p.concepto,
        cantidad: Number(p.cantidad) || 0,
        unidad: p.unidad ?? "ud",
        precio_unitario: Number(p.precio_unitario) || 0,
        importe: (Number(p.cantidad) || 0) * (Number(p.precio_unitario) || 0),
      }));
  const gruposCapitulo = agruparLineasPorCapitulo(lineasOriginales);
  const total = motorActivo
    ? motorTotal
    : partidasGuardadas.reduce(
        (acc, p) => acc + (Number(p.cantidad) || 0) * (Number(p.precio_unitario) || 0),
        0,
      );

  const [editingArea, setEditingArea] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [detailJobcard, setDetailJobcard] = useState(null);

  const intencionByArea = (area) => intenciones.find((i) => i.area === area);

  const jobcardForLine = (area) => {
    const inten = intencionByArea(area);
    if (!inten) return null;
    const candidatas = inten.candidatas ?? [];
    if (candidatas.length === 0) return null;
    const elegida =
      candidatas.find((c) => c.id === inten.jobcard_id) ?? candidatas[0];
    return {
      jobcard_id: elegida.id,
      titulo: elegida.titulo,
      texto: elegida.texto,
      precio_unitario: elegida.precio_unitario,
      coste_total: elegida.coste_total,
      unidad: elegida.unidad,
      familia: elegida.familia,
    };
  };

  const handleSaveEdit = async (choice) => {
    setEditingArea(null);
    await onEditLine(choice);
  };

  const handleAdd = async ({ jobcard, cantidad }) => {
    setAddOpen(false);
    await onAddPartida({ jobcard, cantidad });
  };

  const handleConfirmDelete = async () => {
    const area = confirmDelete;
    setConfirmDelete(null);
    if (motorActivo) {
      await onDeleteLine(area);
    } else if (update) {
      update({
        partidas: partidasGuardadas.filter((p) => p.id !== area),
      });
    }
  };

  return (
    <div data-testid="motor-budget-view">
      {promptEnviado && (
        <div className="mb-4 rounded-md bg-slate-50 border border-slate-200 p-3">
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-1">
            Tu descripción
          </div>
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{promptEnviado}</div>
        </div>
      )}

      <div className="rounded-md border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3 font-bold">Concepto</th>
              <th className="text-right px-4 py-3 font-bold">Cantidad</th>
              <th className="text-right px-4 py-3 font-bold">Precio</th>
              <th className="text-right px-4 py-3 font-bold">Importe</th>
              <th className="px-2 py-3 w-[110px]"></th>
            </tr>
          </thead>
          <tbody>
            {gruposCapitulo.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                  {motorActivo
                    ? "No hay partidas. Vuelve atrás para describir el trabajo."
                    : "Este presupuesto no tiene partidas guardadas."}
                </td>
              </tr>
            )}
            {gruposCapitulo.map((grupo, gIdx) => (
              <Fragment key={`cap-${grupo.capitulo.id}`}>
                <tr
                  className="bg-slate-100 border-t border-slate-300"
                  data-testid={`motor-capitulo-${grupo.capitulo.id}`}
                >
                  <td
                    colSpan={5}
                    className="px-4 py-2 text-[11px] uppercase tracking-widest font-bold text-slate-600"
                  >
                    Capítulo {gIdx + 1} · {grupo.capitulo.nombre}
                  </td>
                </tr>
                {grupo.lineas.map((l) => {
                  const esSugerencia =
                    typeof l.area === "string" && l.area.startsWith("extra_");
                  return (
                    <tr
                      key={l.area}
                      className={`border-t border-slate-200 ${
                        esSugerencia ? "bg-purple-50/30" : ""
                      }`}
                      data-testid={`motor-line-${l.area}`}
                    >
                      <td className="px-4 py-3 text-slate-800">
                        <div className="flex items-center gap-2">
                          {esSugerencia && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold text-purple-700 bg-purple-100 border border-purple-200 shrink-0"
                              title="Añadida desde sugerencias del experto IA"
                              data-testid={`motor-line-badge-${l.area}`}
                            >
                              <Lightbulb className="w-3 h-3" /> IA
                            </span>
                          )}
                          <span className="leading-snug">{l.concepto}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                        {l.cantidad} {l.unidad ?? ""}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                        {fmtEUR(l.precio_unitario)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                        {fmtEUR(l.importe)}
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setDetailJobcard(jobcardForLine(l.area))}
                            disabled={loading || !jobcardForLine(l.area)}
                            className="p-1.5 rounded-md text-slate-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Ver detalle"
                            data-testid={`motor-detail-line-${l.area}`}
                          >
                            <Info className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingArea(l.area)}
                            disabled={loading || !motorActivo || !intencionByArea(l.area)}
                            className="p-1.5 rounded-md text-slate-500 hover:text-blue-600 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={motorActivo ? "Modificar partida" : "Solo disponible con motor activo"}
                            data-testid={`motor-edit-line-${l.area}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(l.area)}
                            disabled={loading}
                            className="p-1.5 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                            title="Eliminar partida"
                            data-testid={`motor-delete-line-${l.area}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
            <tr className="border-t-2 border-slate-300 bg-slate-50">
              <td className="px-4 py-3 font-display font-bold text-slate-800" colSpan={3}>
                Total
              </td>
              <td className="px-4 py-3 text-right tabular-nums font-display font-bold text-blue-700 text-lg">
                {fmtEUR(total)}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          type="button"
          onClick={() => setAddOpen(true)}
          disabled={loading || !motorActivo}
          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
          title={motorActivo ? "Añadir partida" : "Solo disponible con motor activo"}
          data-testid="motor-add-partida-btn"
        >
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-50 hover:bg-blue-100">
            <Plus className="w-4 h-4" />
          </span>
          Añadir partida
        </button>
        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Recalculando…
          </div>
        )}
      </div>

      <EditLineModal
        open={!!editingArea}
        onClose={() => setEditingArea(null)}
        intencion={intencionByArea(editingArea)}
        onSave={handleSaveEdit}
        loading={loading}
      />

      <AddPartidaModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
        loading={loading}
      />

      <JobCardDetailModal
        open={!!detailJobcard}
        onClose={() => setDetailJobcard(null)}
        jobcard={detailJobcard}
      />

      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md" data-testid="motor-confirm-delete-modal">
          <DialogHeader>
            <DialogTitle>Eliminar partida</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            ¿Seguro que quieres eliminar esta partida del presupuesto?
          </p>
          <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-slate-300">
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 gap-2"
              data-testid="motor-confirm-delete-btn"
            >
              <Trash2 className="w-4 h-4" /> Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
